import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { FormulaElementType } from "abreact/expression/formula/framework/FormulaElementType";
import { FormulaUtils } from "abreact/expression/formula/framework/FormulaUtils";
import { OperatorOperandValueImpl } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/OperatorOperandValueImpl";
import { AbImmutableRecord } from "abreact/utils/AbImmutableRecord";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
import * as Immutable from "immutable";
export class TreeNodeContext {
  constructor(formulaElementModel, expressionDomainModel, summaryGenerator) {
    this.formulaElementModel = formulaElementModel;
    this.expressionDomainModel = expressionDomainModel;
    this.summaryGenerator = summaryGenerator;
  }
}
export class BinarySubTree {
  constructor(context, element, left, right) {
    this.context = context;
    this.element = element;
    this.left = left;
    this.right = right;
  }
  get type() {
    return this.element.type;
  }
  toOperatorOperandValue() {
    let operator = FormulaUtils.getOperatorForElementType(this.element.type, this.context.expressionDomainModel, this.context.formulaElementModel.isStringEditor());
    let leftValue = ClassUtils.isInstanceOf(this.left, BinarySubTree) ? this.left.toOperatorOperandValue() : this.left;
    let rightValue = ClassUtils.isInstanceOf(this.right, BinarySubTree) ? this.right.toOperatorOperandValue() : this.right;
    const argumentOperandValuesByOperandName = new Map();
    if (leftValue) {
      argumentOperandValuesByOperandName.set("left", leftValue.withOperandName("left"));
    }
    if (rightValue) {
      argumentOperandValuesByOperandName.set("right", rightValue.withOperandName("right"));
    }
    let operatorOperandValue = new OperatorOperandValueImpl({
      argumentOperandValuesByOperandName: Immutable.Map(argumentOperandValuesByOperandName),
      operandName: "right",
      // lopsided tree -- the operator node is always on the right and the value on the left
      expOperatorObject: AbImmutableRecord.State.Object.from(operator),
      valueCategoryID: operator.output.categoryID,
      valueTypeIDs: Immutable.List.of(operator.output.valueTypeID),
      multiple: operator.output.multiple ?? false,
      id: this.element.id
    });
    operatorOperandValue = operatorOperandValue.withDisplayValue(this.context.summaryGenerator.generateStringSummaryForOperandValue(operatorOperandValue));
    return operatorOperandValue;
  }
}
export class FormulaToExpressionTreeUtils {
  constructor(formulaElementModel, expressionDomainModel, summaryGenerator) {
    _defineProperty(this, "context", void 0);
    _defineProperty(this, "stack", []);
    _defineProperty(this, "output", []);
    this.context = new TreeNodeContext(formulaElementModel, expressionDomainModel, summaryGenerator);
  }
  getOperatorPrecedence(formulaElementType) {
    return formulaElementType === FormulaElementType.OPERATOR_ADD || formulaElementType === FormulaElementType.OPERATOR_SUBTRACT ? 0 : 1;
  }
  peekAtStack() {
    return this.stack.length > 0 ? this.stack[this.stack.length - 1] : undefined;
  }
  addToOutput(value) {
    this.output.push(value);
  }
  convertOperatorOnTopOfStackToBinaryTree() {
    const op = this.stack.pop();
    if (!op) return;
    if (op.type === FormulaElementType.PAREN_LEFT) return;
    if (this.context.formulaElementModel.isOperatorType(op.type)) {
      const right = this.output.pop();
      const left = this.output.pop();
      return new BinarySubTree(this.context, op, left, right);
    }
  }
  handleToken(formulaElement) {
    if (this.context.formulaElementModel.isOperatorType(formulaElement.type)) {
      const o1 = formulaElement;
      let o2 = this.peekAtStack();

      // walk the stack back until there is an operator with
      // a lesser precedence or an open paren and pop it off teh stack
      while (o2 !== undefined && o2.type !== FormulaElementType.PAREN_LEFT && this.getOperatorPrecedence(o2.type) >= this.getOperatorPrecedence(o1.type)) {
        this.addToOutput(this.convertOperatorOnTopOfStackToBinaryTree());
        o2 = this.peekAtStack();
      }
      // now that there is no lower precedence operator there, put the operator on the stack
      this.stack.push(o1);
      return;
    }
    switch (formulaElement.type) {
      case FormulaElementType.VALUE_NODE:
        this.addToOutput(this.context.formulaElementModel.findOperandValueForElement(formulaElement));
        break;
      case FormulaElementType.PAREN_LEFT:
        this.stack.push(formulaElement);
        break;
      case FormulaElementType.PAREN_RIGHT:
        let topOfStack = this.peekAtStack();
        while (topOfStack != null && topOfStack.type !== FormulaElementType.PAREN_LEFT) {
          this.addToOutput(this.convertOperatorOnTopOfStackToBinaryTree());
          topOfStack = this.peekAtStack();
        }
        if (topOfStack?.type === FormulaElementType.PAREN_LEFT) {
          this.convertOperatorOnTopOfStackToBinaryTree(); // discard the open paren
        }
        break;
    }
  }
  createTree() {
    for (let formulaElement of this.context.formulaElementModel.getNoPlaceHolderElements()) {
      this.handleToken(formulaElement);
    }
    while (this.stack.length !== 0) {
      const potentialOp = this.convertOperatorOnTopOfStackToBinaryTree();
      if (potentialOp) {
        this.addToOutput(potentialOp);
      }
    }
    Assert.assert(this.output[0] != null);
    return this.output[0];
  }
}
//# sourceMappingURL=FormulaToExpressionTreeUtils.js.map
